import { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config/env";

// The two announcement boxes at the top of the home page (text1 = orange, text2 = pink), edited
// from the admin page. Three things keep them fast:
//   1. the last banner this browser saw is kept in localStorage and drawn on the first render,
//   2. public/index.html starts the request before the app bundle has even loaded,
//   3. the request is a plain fetch with no Authorization header, so there is no CORS preflight.
// The fresh answer replaces the remembered one as soon as it lands.

export interface HomeBannerText {
  text1: string;
  text2: string;
}

declare global {
  interface Window {
    // set by the inline script in public/index.html; consumed once
    __n450sHomeBanner?: Promise<unknown> | null;
  }
}

const STORAGE_KEY = "n450s.homeBanner";
const EMPTY: HomeBannerText = { text1: "", text2: "" };

function normalize(raw: unknown): HomeBannerText | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    text1: typeof r.text1 === "string" ? r.text1 : "",
    text2: typeof r.text2 === "string" ? r.text2 : "",
  };
}

export function readRememberedHomeBanner(): HomeBannerText | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? normalize(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
}

export function rememberHomeBanner(banner: HomeBannerText): void {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ text1: banner.text1, text2: banner.text2 }),
    );
  } catch {
    // private mode or full storage: the banner just won't be instant next time
  }
}

function fetchFresh(): Promise<unknown> {
  return fetch(`${API_BASE_URL}/home-banner`).then((r) => (r.ok ? r.json() : null));
}

function fetchHomeBanner(): Promise<unknown> {
  // the early request only matches the page load that started it; later visits to the home page
  // inside the SPA ask again
  const early = typeof window !== "undefined" ? window.__n450sHomeBanner : null;
  if (typeof window !== "undefined") window.__n450sHomeBanner = null;
  if (early) return early.then((raw) => raw ?? fetchFresh());
  return fetchFresh();
}

export function useHomeBanner(): HomeBannerText {
  const [banner, setBanner] = useState<HomeBannerText | null>(readRememberedHomeBanner);

  useEffect(() => {
    let live = true;
    fetchHomeBanner()
      .then((raw) => {
        const next = normalize(raw);
        if (!next) return;
        rememberHomeBanner(next);
        if (live) setBanner(next);
      })
      .catch(() => {
        // offline or backend down: keep whatever we remembered
      });
    return () => {
      live = false;
    };
  }, []);

  return banner ?? EMPTY;
}
